// Inventory · Supply & Demand.
// Answers, per material: when will I run short, what should I stock, and am I using this
// steadily enough to buy it in bigger lots?
//
// Supply is what is on hand and what is on order, placed in the month it is expected.
// Demand is stacked in three visibly different tiers: firm (locked jobs), committed (won,
// not locked) and likely (not yet won, multiplied by the opportunity's likelihood). The
// horizon is the jobs' build windows from the Landing Board. Usage is what cards actually
// consumed over the trailing months.

import { Fragment, useState } from 'react';
import type { SdMonth, SdRow } from '@zerogap/engine';
import { type Row } from '../../lib/api';
import { qty, shortDate } from '../../lib/format';
import { draftFor, materialRequests, putOnOrder, useInventory, useVendors, type InventoryData } from '../../lib/inventory';
import { useSession } from '../../lib/session';
import { Btn, Empty, Info, Loading, Panel, Tag } from '../../ui/kit';
import { PurchaseOrder } from './PurchaseOrder';
import { cap, useInventoryNames, type InventoryNames } from './names';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthName = (key: string) => `${MONTHS[Number(key.slice(5, 7)) - 1]} ${key.slice(2, 4)}`;
const some = (v: string | null | undefined) => v !== null && v !== undefined && Number(v) !== 0;

export function SupplyDemand({ openMaterial }: { openMaterial: (id: string) => void }) {
  const s = useSession();
  const names = useInventoryNames();
  const vendors = useVendors();
  const inv = useInventory();
  const [filter, setFilter] = useState('');
  const [all, setAll] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [orderId, setOrderId] = useState<string | null>(null);
  const canBuy = s.canEdit('screen.purchasing');
  if (inv.loading) return <Loading what="Laying supply against demand" />;

  const row = (m: Row) => inv.sd.get(m.id)!;
  const active = (r: SdRow) => some(r.firm) || some(r.committed) || some(r.likely) || some(r.on_order);
  const tracked = (m: Row) => active(row(m)) || m.safety_stock !== null || Number(m.stock_on_hand) !== 0 || row(m).usage.some((u) => some(u.qty));
  const pool = inv.materials.filter((m) => inv.sd.has(m.id)).filter((m) => (filter ? true : all ? tracked(m) : active(row(m))));
  const shown = pool
    .filter((m) => !filter || `${m.description} ${m.part_number ?? ''} ${vendors.name(m.vendor_id) ?? ''}`.toLowerCase().includes(filter.toLowerCase()))
    // what runs short soonest comes first
    .sort((a, b) => (row(a).first_short ?? '9999').localeCompare(row(b).first_short ?? '9999') || (row(a).first_short_with_likely ?? '9999').localeCompare(row(b).first_short_with_likely ?? '9999') || String(a.description).localeCompare(String(b.description)))
    .slice(0, 150);
  // every row shows the same months: this month out to the last build window on screen
  const months = [...new Set(shown.flatMap((m) => row(m).months.map((x) => x.month)))].sort();
  const anyUndated = shown.some((m) => some(row(m).undated.firm) || some(row(m).undated.committed) || some(row(m).undated.likely));

  const startOrder = async (m: Row) => {
    if (!m.vendor_id) return;
    const r = row(m);
    const ready = materialRequests(inv, m.id, `under ${names.safety_stock}`);
    // nothing waiting to be ordered: a line for the shelf, at the order quantity, to be set on the draft
    const requests = ready.length > 0 ? ready : [{ kind: 'reorder' as const, material_id: m.id as string, qty: Number(r.shortfall) > 0 ? Number(r.shortfall) : Number(m.order_quantity ?? 1), est_unit_price: m.price === null ? null : Number(m.price), note: `started from ${names.supply_demand}` }];
    setOrderId(await putOnOrder(m.vendor_id, requests, draftFor(inv, m.vendor_id)?.po.id));
  };

  return (
    <div className="stack">
      <div className="row mid" style={{ justifyContent: 'space-between' }}>
        <small>
          <span className="sd firm">{names.firm}</span> <span className="sd committed">{names.committed}</span> <span className="sd likely">{names.likely} × likelihood</span> <span className="sd in">+ arriving</span>
          <Info>
            Each month shows what jobs will draw, in three tiers, and what is expected to arrive. <b>{cap(names.firm)}</b>: jobs with a locked revision. <b>{cap(names.committed)}</b>: won, not locked yet. <b>{cap(names.likely)}</b>: not won yet, counted at the
            opportunity's likelihood (a 40% job needing 10 counts as 4). A job's demand falls in the month its build window starts. The number under them is what would be left on the shelf at the end of that month on {names.firm} and {names.committed} demand; it turns
            amber when {names.likely} demand would take it below zero, and red when it is below zero without it.
          </Info>
        </small>
        <span className="row mid" style={{ gap: 8 }}>
          <label className="check">
            <input type="checkbox" className="read" checked={all} onChange={(e) => setAll(e.target.checked)} /> also what is stocked or was used
          </label>
          <input type="search" placeholder="Find a material" value={filter} onChange={(e) => setFilter(e.target.value)} />
        </span>
      </div>

      <Panel label={`${shown.length} ${shown.length === 1 ? 'material' : 'materials'}${pool.length > shown.length ? ` of ${pool.length}` : ''} · this month to ${months.length ? monthName(months[months.length - 1]!) : 'now'}`}>
        {shown.length === 0 ? (
          <Empty>{filter ? 'No material matches.' : 'Nothing is in demand and nothing is on order. Demand appears here as soon as a job has items, weighted by how likely it is.'}</Empty>
        ) : (
          <div className="tblwrap">
            <table className="sdtable">
              <thead>
                <tr>
                  <th>Material</th>
                  <th className="right">On hand</th>
                  <th className="right">On order</th>
                  {months.map((k) => (
                    <th key={k}>{monthName(k)}</th>
                  ))}
                  {anyUndated && (
                    <th>
                      No date
                      <Info left>Demand from jobs that have no build window on the Landing Board yet. It counts toward what is short overall, but cannot be placed in a month.</Info>
                    </th>
                  )}
                  <th>Runs short</th>
                  <th className="right">
                    Used / month
                    <Info left>What closed cards consumed, averaged over the last {inv.usageMonths} months (the window is a setting). Steady use is the case for buying in bigger lots or keeping it on the shelf.</Info>
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {shown.map((m) => {
                  const r = row(m);
                  const byMonth = new Map(r.months.map((x) => [x.month, x]));
                  // months past this material's own horizon carry its last balance forward
                  const last = r.months[r.months.length - 1];
                  return (
                    <Fragment key={m.id}>
                      <tr className="click" onClick={() => setOpen(open === m.id ? null : m.id)}>
                        <td>
                          <button
                            type="button"
                            className="linklike"
                            onClick={(e) => {
                              e.stopPropagation();
                              openMaterial(m.id);
                            }}
                          >
                            {m.description}
                          </button>{' '}
                          {m.tracking_mode === 'bin' && <Tag kind="off">bin</Tag>}
                          <br />
                          <small>
                            {vendors.name(m.vendor_id) ?? 'no vendor'} · {m.unit ?? 'no unit'}
                            {m.safety_stock !== null && ` · ${names.safety_stock} ${qty(m.safety_stock)}`}
                          </small>
                        </td>
                        <td className="right num">{r.counted ? qty(r.on_hand) : '—'}</td>
                        <td className="right num">
                          {some(r.on_order) ? qty(r.on_order) : '—'}
                          {some(r.on_order_undated) && (
                            <div>
                              <small>{qty(r.on_order_undated)} with no date</small>
                            </div>
                          )}
                        </td>
                        {months.map((k) => (
                          <td key={k}>
                            <MonthCell x={byMonth.get(k)} carried={last && k > last.month ? last : undefined} />
                          </td>
                        ))}
                        {anyUndated && (
                          <td>
                            <Chips firm={r.undated.firm} committed={r.undated.committed} likely={r.undated.likely} />
                          </td>
                        )}
                        <td className="nowrap">
                          {r.first_short ? (
                            <span className="bad">
                              <b>{monthName(r.first_short)}</b>
                            </span>
                          ) : r.first_short_with_likely ? (
                            <span className="warn">{monthName(r.first_short_with_likely)} if the {names.likely} jobs land</span>
                          ) : some(r.shortfall) ? (
                            <span className="warn">short {qty(r.shortfall)} overall</span>
                          ) : r.counted ? (
                            <small>covered</small>
                          ) : (
                            <small>not counted</small>
                          )}
                        </td>
                        <td className="right num">{some(r.usage_per_month) ? qty(r.usage_per_month) : '—'}</td>
                        <td className="nowrap">
                          {canBuy &&
                            (m.vendor_id ? (
                              <Btn
                                small
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void startOrder(m);
                                }}
                              >
                                Start an order
                              </Btn>
                            ) : (
                              <small>no vendor</small>
                            ))}
                        </td>
                      </tr>
                      {open === m.id && (
                        <tr>
                          <td colSpan={7 + months.length + (anyUndated ? 1 : 0) - 1}>
                            <Detail inv={inv} m={m} r={r} names={names} openOrder={setOrderId} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      {orderId && <PurchaseOrder orderId={orderId} onClose={() => setOrderId(null)} openMaterial={openMaterial} />}
    </div>
  );
}

function Chips({ firm, committed, likely, arriving }: { firm: string; committed: string; likely: string; arriving?: string }) {
  return (
    <>
      {some(firm) && <span className="sd firm">{qty(firm)}</span>}
      {some(committed) && <span className="sd committed">{qty(committed)}</span>}
      {some(likely) && <span className="sd likely">~{qty(likely)}</span>}
      {some(arriving) && <span className="sd in">+{qty(arriving)}</span>}
    </>
  );
}

/** One month for one material: what is drawn, what arrives, and what is left at the end of it. */
function MonthCell({ x, carried }: { x: SdMonth | undefined; carried?: SdMonth }) {
  const names = useInventoryNames();
  const m = x ?? carried;
  if (!m) return null;
  const balance = m.balance === null ? null : Number(m.balance);
  const withLikely = m.balance_with_likely === null ? null : Number(m.balance_with_likely);
  return (
    <div className="sdcell">
      {x && <Chips firm={x.firm} committed={x.committed} likely={x.likely} arriving={x.arriving} />}
      {balance !== null && (
        <div className={`num ${balance < 0 ? 'bad' : withLikely !== null && withLikely < 0 ? 'warn' : 'muted'}`} title={withLikely !== null && withLikely !== balance ? `${qty(withLikely)} if the ${names.likely} jobs land` : undefined}>
          {balance < 0 ? <b>{qty(balance)}</b> : qty(balance)}
        </div>
      )}
    </div>
  );
}

/** Behind one row: which jobs, which orders, and the usage month by month. */
function Detail({ inv, m, r, names, openOrder }: { inv: InventoryData; m: Row; r: SdRow; names: InventoryNames; openOrder: (id: string) => void }) {
  const demand = inv.sdDemand.filter((d) => d.material_id === m.id && Number(d.qty) > 0).sort((a, b) => (a.needed_on ?? '9999').localeCompare(b.needed_on ?? '9999'));
  const arrivals = inv.sdArrivals.filter((a) => a.material_id === m.id);
  const tierName = (t: string) => (t === 'firm' ? names.firm : t === 'committed' ? names.committed : names.likely);
  return (
    <div className="row" style={{ padding: '6px 0 10px' }}>
      <div className="col">
        <div className="lb">
          <span>Demand</span>
        </div>
        {demand.length === 0 && <small>No job needs it.</small>}
        {demand.map((d) => (
          <div key={`${d.job_id}`} className="ln">
            <span>
              {d.jobName} <span className={`sd ${d.tier}`}>{tierName(d.tier)}</span>
              <br />
              <small>{d.needed_on ? `build window starts ${shortDate(d.needed_on)}` : 'no build window yet'}</small>
            </span>
            <span className="right num nowrap">
              {d.tier === 'likely' ? (
                <>
                  {qty(d.qty)} × {d.likelihood_pct === null || d.likelihood_pct === undefined || d.likelihood_pct === '' ? 'no likelihood set' : `${Number(d.likelihood_pct)}%`} = <b>~{qty((Number(d.qty) * Math.min(100, Math.max(0, Number(d.likelihood_pct ?? 0)))) / 100)}</b>
                </>
              ) : (
                <b>{qty(d.qty)}</b>
              )}{' '}
              {m.unit ?? ''}
            </span>
          </div>
        ))}
      </div>
      <div className="col">
        <div className="lb">
          <span>On order</span>
        </div>
        {arrivals.length === 0 && <small>Nothing on order.</small>}
        {arrivals.map((a) => (
          <div key={a.order_id} className="ln click" onClick={() => openOrder(a.order_id)}>
            <span>
              {a.number}
              <br />
              <small>{a.expected_on ? `expected ${shortDate(a.expected_on)}` : 'no expected date: the lead time is not known'}</small>
            </span>
            <span className="right num">
              <b>{qty(a.qty)}</b> {m.unit ?? ''}
            </span>
          </div>
        ))}
        <div className="lb" style={{ marginTop: 10 }}>
          <span>Used, by month</span>
        </div>
        <small>
          {r.usage.map((u) => `${monthName(u.month)}: ${qty(u.qty)}`).join(' · ')}
          {r.usage.length > 0 && ` · average ${qty(r.usage_per_month)} a month`}
        </small>
      </div>
    </div>
  );
}
